/**
 * formatDate.js — America/Chicago timezone-aware date formatting utility
 *
 * ALL display functions pin output to America/Chicago (CST UTC-6 / CDT UTC-5).
 * Node.js on the server also runs with TZ=America/Chicago (set in server.js),
 * so date boundaries (start-of-day, start-of-week, etc.) are consistent
 * everywhere — server, browser, and reports — regardless of where the
 * browser user is physically located.
 *
 * HOW IT WORKS
 * ─────────────
 * MongoDB stores every timestamp as UTC.  When the frontend receives e.g.
 * "2026-06-12T20:35:00.000Z" it must be displayed as Chicago time.
 *
 * • formatDate / formatDateLong / formatDateShort — date-only fields
 *   (checkDate, dateOfBirth, expiryDate).  We extract the YYYY-MM-DD
 *   part *in Chicago time* via Intl.DateTimeFormat, then re-anchor to
 *   local noon so the calendar date never shifts across DST boundaries.
 *
 * • formatDateTime — real timestamps (dateCashed, createdAt, modifiedAt).
 *   Pass the raw UTC value; display it in Chicago time with time included.
 *
 * • getTodayChicago() — returns "YYYY-MM-DD" for *today* in Chicago.
 *   Use this wherever code previously wrote new Date().toISOString().split("T")[0],
 *   because toISOString() always returns UTC, which can be yesterday in Chicago.
 *
 * • chicagoNow() — returns a JS Date whose numeric value equals the
 *   current instant (same as new Date()), but is handy as a named
 *   export to make intent obvious.  For hour-of-day comparisons (greeting)
 *   call getChicagoHour() instead.
 *
 * • getChicagoHour() — current hour (0-23) in Chicago time.
 *   Replaces: new Date().getHours() in Dashboard greeting.
 */

const TZ = 'America/Chicago';

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Given any UTC date value, return its "YYYY-MM-DD" string in Chicago time.
 * e.g. "2026-06-12T03:00:00Z" → "2026-06-11" (still June 11 in Chicago at 9 PM CDT)
 */
function toChicagoDateStr(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;
  // Use Intl to extract the calendar date parts *in Chicago time*
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const p = {};
  parts.forEach(({ type, value: v }) => { p[type] = v; });
  return `${p.year}-${p.month}-${p.day}`; // YYYY-MM-DD
}

/**
 * Safe date anchor for date-only display: extracts the Chicago calendar date
 * then re-parses it as LOCAL noon so toLocaleDateString never shifts the date.
 */
function safeDate(value) {
  const dateStr = toChicagoDateStr(value);
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T12:00:00'); // local noon — safe for all TZs
  return isNaN(d) ? null : d;
}

// ── Public date-only formatters ───────────────────────────────────────────────

/** MM/DD/YYYY — table cells and detail fields */
export function formatDate(value) {
  const d = safeDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/** Wednesday, June 3, 2026 — page header subtitles */
export function formatDateLong(value) {
  const d = safeDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

/** Jun 3, 2026 — modal subtitles */
export function formatDateShort(value) {
  const d = safeDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Timestamp formatter ───────────────────────────────────────────────────────

/**
 * Smart timestamp formatter for dateCashed in America/Chicago time.
 *
 * BACKGROUND — why some records show 07:00 AM:
 * Old records were stored as UTC noon (T12:00:00.000Z) — a "date-only anchor"
 * used to avoid timezone shifts.  UTC noon = 07:00 AM Chicago CDT (UTC-5).
 * These records have no real time; showing "07:00 AM" is misleading noise.
 *
 * New records (after TZ=America/Chicago was set on the server) are stored as
 * Chicago noon (which is UTC 17:00 in CDT) — also a date-only anchor.
 *
 * RULE:
 * • If the UTC time component is exactly :00:00.000 on any of the known
 *   anchor hours (12 = old UTC noon, 17 = Chicago noon CDT, 18 = Chicago noon CST),
 *   treat it as a date-only record → show MM/DD/YYYY only.
 * • Otherwise the record has a real wall-clock time → show MM/DD/YYYY, HH:MM AM/PM.
 *
 * Output examples:
 *   "2026-06-17T12:00:00.000Z"  → "06/17/2026"           (old UTC-noon anchor)
 *   "2026-06-17T17:00:00.000Z"  → "06/17/2026"           (Chicago noon CDT anchor)
 *   "2026-06-17T18:00:00.000Z"  → "06/17/2026"           (Chicago noon CST anchor)
 *   "2026-06-17T20:35:00.000Z"  → "06/17/2026, 03:35 PM" (real timestamp CDT)
 */
export function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';

  // Detect date-only anchor: seconds and ms must be zero, and UTC hour must
  // be one of the known anchor values (12 = old UTC noon; 17 or 18 = Chicago noon).
  const utcH  = d.getUTCHours();
  const utcM  = d.getUTCMinutes();
  const utcS  = d.getUTCSeconds();
  const utcMs = d.getUTCMilliseconds();
  const isDateOnlyAnchor =
    utcM === 0 && utcS === 0 && utcMs === 0 &&
    (utcH === 12 || utcH === 17 || utcH === 18);

  if (isDateOnlyAnchor) {
    // Show date only — no misleading time
    return d.toLocaleDateString('en-US', {
      timeZone: TZ,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  // Real timestamp — show full date + time in Chicago
  return d.toLocaleString('en-US', {
    timeZone: TZ,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

// ── "Now" helpers for Chicago ─────────────────────────────────────────────────

/**
 * Returns today's date as "YYYY-MM-DD" in Chicago time.
 * Use instead of: new Date().toISOString().split("T")[0]
 * (toISOString is always UTC and can be yesterday's date in Chicago)
 */
export function getTodayChicago() {
  return toChicagoDateStr(new Date());
}

/**
 * Returns the current hour (0–23) in Chicago time.
 * Use instead of: new Date().getHours() for time-of-day greetings.
 */
export function getChicagoHour() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(new Date());
  const hourPart = parts.find(p => p.type === 'hour');
  return hourPart ? Number(hourPart.value) % 24 : new Date().getHours();
}

/**
 * Returns "Wed, Jun 17, 2026" for the topbar header, in Chicago time.
 * Use instead of: new Date().toLocaleDateString(...)
 */
export function getTopbarDateChicago() {
  return new Date().toLocaleDateString('en-US', {
    timeZone: TZ,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Date-range preset helper — returns { from, to } as "YYYY-MM-DD" strings
 * based on *Chicago* "today".  Replaces the getPreset() function in Reports.js
 * that used new Date().toISOString() (UTC-based, wrong date late at night).
 */
export function getChicagoPreset(key) {
  const todayStr = getTodayChicago();               // "YYYY-MM-DD" in Chicago
  const today = new Date(todayStr + 'T12:00:00');   // local noon anchor

  const iso = (d) => {
    // Extract YYYY-MM-DD from a local-noon-anchored Date
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dy}`;
  };

  switch (key) {
    case 'today':
      return { from: todayStr, to: todayStr };

    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      return { from: iso(d), to: iso(d) };
    }

    case 'this_week': {
      // Sunday-based week
      const d = new Date(today);
      d.setDate(d.getDate() - d.getDay());
      return { from: iso(d), to: todayStr };
    }

    case 'last_week': {
      const end = new Date(today);
      end.setDate(end.getDate() - end.getDay() - 1); // last Saturday
      const start = new Date(end);
      start.setDate(start.getDate() - 6);            // last Sunday
      return { from: iso(start), to: iso(end) };
    }

    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: iso(start), to: todayStr };
    }

    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end   = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: iso(start), to: iso(end) };
    }

    case 'this_year':
      return { from: `${today.getFullYear()}-01-01`, to: todayStr };

    case 'last_year': {
      const y = today.getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }

    default:
      return { from: '', to: '' };
  }
}

// ── Name helpers (unchanged) ──────────────────────────────────────────────────

/** Uppercase all — e.g. "john doe" → "JOHN DOE" */
export function capitalizeName(str) {
  if (!str) return '';
  return str.toUpperCase();
}

/** Full name in ALL CAPS */
export function formatName(firstName, lastName, middleInitial) {
  const f = (firstName || '').toUpperCase();
  const m = middleInitial ? ` ${middleInitial.toUpperCase()}.` : '';
  const l = (lastName || '').toUpperCase();
  return `${f}${m} ${l}`.trim();
}
